import {Component, ExpressionPath, Expressions} from '@packages/systems/wfdl';
import {Err, Ok, Result} from '@packages/utilities/fp/result';
import {
  getBody,
  getCurrentComponentRender,
  getPageComponentRender,
  isComponentRoot,
  type RelativePositionPlacementOptions,
} from '@packages/utilities/platform';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {getRootElementWithFocusedInstancesExpanded} from '@packages/utilities/platform-selectors';
import {constant} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';

const alteringBody = <
  Payload extends {id: string; componentMap: Component.ComponentMap},
>(
  payload: Payload
): Result<string, Payload> => {
  const body = getBody(payload.componentMap);
  return body && payload.id === body.id
    ? Err('Cannot move or remove body node')
    : Ok(payload);
};

const missingElement = <
  Payload extends {
    id: string;
    componentMap: Component.ComponentMap;
    focusedSymbolNativeIdPath: NativeId[];
  },
>(
  payload: Payload
): Result<string, Payload> => {
  //Check if the element exists in the page component
  const page = getPageComponentRender(payload.componentMap);
  if (page && Expressions.getElementById(page, payload.id)) {
    return Ok(payload);
  }

  //If it's not in the page component it could be in the focused component
  if (!payload.focusedSymbolNativeIdPath.length) {
    return Err(`Missing element with id "${payload.id}"`);
  }

  const currentComponent = getCurrentComponentRender(
    payload.componentMap,
    payload.focusedSymbolNativeIdPath
  );

  return currentComponent &&
    Expressions.getElementById(currentComponent, payload.id)
    ? Ok(payload)
    : Err(`Missing element with id "${payload.id}"`);
};

const nextParent = (
  anchorId: string,
  position: RelativePositionPlacementOptions,
  root: Expression
) =>
  position === 'after' || position === 'before'
    ? Expressions.getParent(root, anchorId)
    : Expressions.getElementById(root, anchorId);

const misplacingComponentRoot = (
  payload: ValidateElementMovePayload
): Result<string, ValidateElementMovePayload> => {
  const root = getRootElementWithFocusedInstancesExpanded(
    payload.componentMap,
    payload.focusedSymbolNativeIdPath
  );
  if (!root) {
    return Err('Component root not found.');
  }
  const element = Expressions.getElementById(root, payload.id);
  const nextParentElement = nextParent(
    payload.anchorId,
    payload.position,
    root
  );
  return element &&
    isComponentRoot(payload.componentMap)(element) &&
    nextParentElement &&
    !isComponentRoot(payload.componentMap)(nextParentElement)
    ? Err('Cannot place symbol root except as child of root node.')
    : Ok(payload);
};

const selfAnchor = (
  payload: ValidateElementMovePayload
): Result<string, ValidateElementMovePayload> =>
  payload.id === payload.anchorId
    ? Err('Cannot move a node relative to itself.')
    : Ok(payload);

type ValidateElementMovePayload = {
  id: string;
  anchorId: string;
  anchorPath?: ExpressionPath | null;
  position: RelativePositionPlacementOptions;
  componentMap: Component.ComponentMap;
  focusedSymbolNativeIdPath: NativeId[];
};

export const validateElementMove = weakMemo(
  (payload: ValidateElementMovePayload): Result<string, true> =>
    selfAnchor(payload)
      .chain(missingElement)
      .chain(alteringBody)
      .chain(misplacingComponentRoot)
      .map(constant(true))
);

const anchorInGivenRoot = <Payload extends {anchorId: string}>(
  payload: Payload,
  anchorRoot: Expression
): Result<string, Payload> => {
  if (Expressions.getElementById(anchorRoot, payload.anchorId)) {
    return Ok(payload);
  }
  return Err('Missing anchor node.');
};

/**
 * The duplicate check, scoped the same way the anchor check is: an anchorRoot
 * names the one tree the placement lands in, so that is the tree an id can
 * collide in.
 */
const elementNotInGivenRoot = <Payload extends {element: ExpressionElement}>(
  payload: Payload,
  anchorRoot: Expression
): Result<string, Payload> => {
  if (Expressions.getElementById(anchorRoot, payload.element.id)) {
    return Err(`Node with id “${payload.element.id}” already exists.`);
  }
  return Ok(payload);
};

const missingAnchor = <
  Payload extends {
    anchorId: string;
    componentMap: Component.ComponentMap;
    focusedSymbolNativeIdPath: NativeId[];
  },
>(
  payload: Payload
): Result<string, Payload> => {
  //Check if the element exists in the page component
  const page = getPageComponentRender(payload.componentMap);
  if (page && Expressions.getElementById(page, payload.anchorId)) {
    return Ok(payload);
  }

  //If it's not in the page component it could be in the focused component
  if (!payload.focusedSymbolNativeIdPath.length) {
    return Err('Missing anchor node.');
  }
  const root = getRootElementWithFocusedInstancesExpanded(
    payload.componentMap,
    payload.focusedSymbolNativeIdPath
  );
  return root && Expressions.getElementById(root, payload.anchorId)
    ? Ok(payload)
    : Err('Missing anchor node.');
};

const existingElement = <
  Payload extends {
    element: ExpressionElement;
    componentMap: Component.ComponentMap;
    focusedSymbolNativeIdPath: NativeId[];
  },
>(
  payload: Payload
): Result<string, Payload> => {
  //Check if the element exists in the page component
  const page = getPageComponentRender(payload.componentMap);
  if (page && Expressions.getElementById(page, payload.element.id)) {
    return Err(`Node with id “${payload.element.id}” already exists.`);
  }

  //If it's not in the page component it could be in the focused component
  if (!payload.focusedSymbolNativeIdPath.length) {
    return Ok(payload);
  }
  const currentComponent = getCurrentComponentRender(
    payload.componentMap,
    payload.focusedSymbolNativeIdPath
  );
  return currentComponent &&
    Expressions.getElementById(currentComponent, payload.element.id)
    ? Err(`Node with id “${payload.element.id}” already exists.`)
    : Ok(payload);
};

export const validateElementAdd = weakMemo(
  <
    Payload extends {
      element: ExpressionElement;
      anchorId: string;
      anchorRoot?: Expression;
      componentMap: Component.ComponentMap;
      focusedSymbolNativeIdPath: NativeId[];
    },
  >(
    payload: Payload
  ): Result<string, true> => {
    const {anchorRoot} = payload;
    // The mover writes into anchorRoot when supplied, which can be a component
    // render or Slot default. Checking another tree could approve an anchor
    // the mover cannot find, so both checks must stay within this root.
    if (anchorRoot) {
      return anchorInGivenRoot(payload, anchorRoot)
        .chain((checked) => elementNotInGivenRoot(checked, anchorRoot))
        .map(constant(true));
    }

    return missingAnchor(payload).chain(existingElement).map(constant(true));
  }
);
